import { ArrowRight, Clock, Gauge, BookOpen, ExternalLink, Youtube, Ghost, Printer, Gift, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import PrintSheet, { PRINT_STYLES } from "@/components/PrintSheet";
import { BISCOTTO_SETTIMANA, ARCHIVIO_BISCOTTI, YOUTUBE, IMAGES, LINKS } from "@/data/content";

// Stampa nativa compatibile con mobile: niente popup, usa l'area .print-only
const printScheda = () => {
  try {
    window.print();
  } catch {
    toast.error("Stampa non riuscita: apri il menu del browser e scegli «Stampa».");
  }
};

const Biscotti = () => (
  <div data-testid="section-biscotti" className="mx-auto max-w-md px-5 pb-10 pt-14 sm:max-w-2xl sm:pt-20 lg:max-w-5xl lg:px-8">
    <Reveal>
      <Eyebrow>Il Tutorial Gratuito della Settimana</Eyebrow>
      <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-cacao sm:text-5xl">
        {BISCOTTO_SETTIMANA.nome}
      </h1>
      <p className="mt-3 max-w-lg text-base text-cacao-muted leading-relaxed">
        {BISCOTTO_SETTIMANA.sottotitolo}
        {BISCOTTO_SETTIMANA.steps.length > 0 &&
          ": la guida passo-passo gratuita per dare vita al tuo primo personaggio."}
      </p>
      <div className="mt-4 flex flex-wrap gap-3 text-xs font-semibold">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-menta px-3 py-1.5 text-menta-dark">
          <Gift className="h-3.5 w-3.5" /> Tutorial gratuito
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-menta px-3 py-1.5 text-menta-dark">
          <Gauge className="h-3.5 w-3.5" /> {BISCOTTO_SETTIMANA.difficolta}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-oro-light px-3 py-1.5 text-oro">
          <Clock className="h-3.5 w-3.5" /> {BISCOTTO_SETTIMANA.tempo}
        </span>
        {BISCOTTO_SETTIMANA.tema && (
          <span
            data-testid="biscotto-tema-badge"
            className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-3 py-1.5 text-orange-700"
          >
            <Ghost className="h-3.5 w-3.5" /> {BISCOTTO_SETTIMANA.tema}
          </span>
        )}
        {BISCOTTO_SETTIMANA.steps.length > 0 && (
          <button
            data-testid="print-scheda-button"
            onClick={printScheda}
            className="inline-flex items-center gap-1.5 rounded-full border border-rosa/40 bg-white px-4 py-1.5 font-bold text-rosa-dark transition-colors hover:bg-blush"
          >
            <Printer className="h-3.5 w-3.5" /> Stampa la scheda
          </button>
        )}
      </div>
    </Reveal>

    <Reveal delay={0.1}>
      <div className="relative mt-10 overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_25px_60px_rgba(45,36,36,0.16)]">
        <div className="spotlight absolute inset-0" />
        {BISCOTTO_SETTIMANA.image ? (
          <>
            <img
              src={IMAGES[BISCOTTO_SETTIMANA.image]}
              alt={`${BISCOTTO_SETTIMANA.nome} modellata a mano in pasta di zucchero, il biscotto della settimana`}
              className="aspect-[16/10] w-full object-cover object-[50%_58%] sm:aspect-[21/9]"
              data-testid="biscotto-hero-image"
            />
            <div className="absolute bottom-4 left-4 rounded-xl bg-white/90 px-4 py-2 backdrop-blur">
              <p className="font-hand text-xl text-rosa-dark">{BISCOTTO_SETTIMANA.tagline}</p>
            </div>
          </>
        ) : (
          <div
            data-testid="biscotto-hero-decor"
            className="flex aspect-[16/10] w-full flex-col items-center justify-center gap-3 px-6 text-center sm:aspect-[21/9]"
            style={{ background: "linear-gradient(135deg,#FCE7F0 0%,#FFFBEB 50%,#E8F5E9 100%)" }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-rosa-dark">
              Il tutorial gratuito della settimana
            </p>
            <p className="font-hand text-5xl text-rosa-dark sm:text-7xl">{BISCOTTO_SETTIMANA.tagline}</p>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cacao-light">
              la foto resta segreta fino all'ultimo... che sorpresa!
            </p>
          </div>
        )}
      </div>
    </Reveal>

    {BISCOTTO_SETTIMANA.steps.length > 0 ? (
      <div className="mt-14 space-y-4">
        {BISCOTTO_SETTIMANA.steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.06}>
            <div
              data-testid={`biscotto-step-${s.n}`}
              className="group flex gap-5 rounded-3xl border border-pink-100 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(45,36,36,0.1)] sm:p-7"
            >
              <span className="font-display text-5xl font-extrabold leading-none text-blush transition-colors group-hover:text-rosa sm:text-6xl">
                {s.n}
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-cacao sm:text-xl">{s.title}</h3>
                <p className="mt-1.5 text-sm text-cacao-muted leading-relaxed">{s.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    ) : (
      <Reveal delay={0.1}>
        <div
          data-testid="biscotto-testo"
          className="mt-10 rounded-[2rem] border border-pink-100 bg-white p-7 sm:p-9"
        >
          <p className="text-base text-cacao-muted leading-relaxed">{BISCOTTO_SETTIMANA.testo}</p>
          <p className="mt-4 font-hand text-3xl text-rosa-dark">{BISCOTTO_SETTIMANA.tagline}</p>
        </div>
      </Reveal>
    )}

    {BISCOTTO_SETTIMANA.steps.length > 0 && (
      <PrintSheet
        id="print-sheet-biscotto"
        eyebrow={`IL TUTORIAL GRATUITO DELLA SETTIMANA${BISCOTTO_SETTIMANA.tema ? ` · ${BISCOTTO_SETTIMANA.tema.toUpperCase()}` : ""}`}
        title={BISCOTTO_SETTIMANA.nome}
        subtitle={`${BISCOTTO_SETTIMANA.sottotitolo} · Difficoltà: ${BISCOTTO_SETTIMANA.difficolta} · Tempo: ${BISCOTTO_SETTIMANA.tempo}`}
      >
        <h2 style={PRINT_STYLES.h2}>La guida passo-passo</h2>
        <ol style={PRINT_STYLES.list}>
          {BISCOTTO_SETTIMANA.steps.map((s) => (
            <li key={s.n} style={{ marginBottom: 10 }}>
              <strong>{s.n} — {s.title}.</strong> {s.text}
            </li>
          ))}
        </ol>
      </PrintSheet>
    )}

    <Reveal delay={0.1}>
      <div
        data-testid="halloween-hook"
        className="mt-14 overflow-hidden rounded-[2rem] border border-emerald-100 p-7 sm:p-9"
        style={{ background: "linear-gradient(100deg,#E8F5E9 0%,#FFFDF9 55%,#FCE7F0 100%)" }}
      >
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-menta">
              <Snowflake className="h-6 w-6 text-menta-dark" />
            </span>
            <div>
              <p className="font-display text-xl font-bold text-cacao">
                E dopo Halloween... arriva il Natale!
              </p>
              <p className="mt-1.5 max-w-xl text-sm text-cacao-muted leading-relaxed">
                {BISCOTTO_SETTIMANA.hook}
              </p>
              <p className="mt-3 space-y-1">
                <a
                  data-testid="pentolone-tutorial-cta"
                  href="https://youtu.be/wr0hjdFn8OE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-hand text-2xl text-rosa-dark underline decoration-rosa/40 underline-offset-4 transition-colors hover:text-rosa"
                >
                  guarda il tutorial del Pentolone della Strega!
                </a>
                <a
                  data-testid="zucca-tutorial-cta"
                  href="https://youtu.be/JyoTQrmfcQQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-hand text-2xl text-rosa-dark underline decoration-rosa/40 underline-offset-4 transition-colors hover:text-rosa"
                >
                  e anche quello della Zucca in 3D!
                </a>
              </p>
              <p className="mt-2 text-xs font-semibold text-cacao-light">
                Lascia la tua email qui sotto e non perdere i tutorial di Natale in arrivo.
              </p>
            </div>
          </div>
          <img
            src={IMAGES.pentolone}
            alt="Pentolone della Strega in pasta di zucchero con bollicine verdi e pipistrello, davanti al camino"
            data-testid="halloween-hook-photo"
            className="mx-auto w-40 shrink-0 -rotate-2 rounded-2xl border-4 border-white object-cover object-[50%_50%] shadow-[0_14px_35px_rgba(45,36,36,0.16)] sm:w-44"
            style={{ aspectRatio: "4/5" }}
          />
        </div>
      </div>
    </Reveal>

    <Reveal delay={0.1}>
      <div
        data-testid="ebook-banner"
        className="relative mt-14 overflow-hidden rounded-[2rem] bg-cacao p-8 text-center sm:p-12"
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(ellipse 80% 70% at 50% 0%, rgba(232,122,144,0.35), transparent 70%)" }}
        />
        <div className="relative">
          <span className="inline-block rounded-full bg-oro px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-white">
            Offerta Speciale
          </span>
          <h3 className="mt-5 font-display text-2xl font-extrabold text-crema sm:text-3xl">
            Biscotti Senza Stress: la collezione
          </h3>
          <p className="mx-auto mt-3 max-w-md text-sm text-crema/70 leading-relaxed">
            Gli Ebook completi con schemi dettagliati, cartamodelli stampabili
            e tutorial gratuito incluso: animali della fattoria, mamma, papà e
            i nonni.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              data-testid="ebook-cta-fattoria"
              href={LINKS.ebookFattoria}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full bg-rosa px-7 py-3.5 text-sm font-bold text-white shadow-[0_12px_35px_rgba(232,122,144,0.5)] transition-colors hover:bg-rosa-dark"
            >
              <BookOpen className="h-4 w-4" />
              Animali della Fattoria
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              data-testid="ebook-cta-famiglia"
              href={LINKS.ebookFamiglia}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full border border-crema/30 bg-white/10 px-7 py-3.5 text-sm font-bold text-crema backdrop-blur transition-colors hover:border-rosa hover:text-blush"
            >
              <BookOpen className="h-4 w-4" />
              Mamma, Papà e i Nonni
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </div>
    </Reveal>

    <Reveal delay={0.1}>
      <div data-testid="youtube-tutorial" className="mt-16">
        <Eyebrow>Video Tutorial</Eyebrow>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-cacao sm:text-3xl">
          Guarda il tutorial in video
        </h2>
        <p className="mt-2 max-w-lg text-sm text-cacao-muted leading-relaxed">
          Segui Giorgia passo passo nel video e scopri tutti gli altri
          tutorial sul canale YouTube Le Allegre Pasticcione.
        </p>
        <div className="mt-6 overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_20px_50px_rgba(45,36,36,0.14)]">
          <div className="relative aspect-video w-full">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${YOUTUBE.videoId}`}
              title="Video tutorial di decorazione biscotti in pasta di zucchero — Le Allegre Pasticcione"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 h-full w-full"
              data-testid="youtube-iframe"
            />
          </div>
        </div>
        <a
          data-testid="youtube-channel-cta"
          href={YOUTUBE.channel}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-4 inline-flex items-center gap-2 text-sm font-bold text-rosa-dark transition-colors hover:text-rosa"
        >
          <Youtube className="h-4 w-4" />
          Iscriviti al canale per non perdere i prossimi tutorial
        </a>
      </div>
    </Reveal>

    <Reveal delay={0.1}>
      <div
        data-testid="gufetto-highlight"
        className="mt-8 grid items-center gap-6 overflow-hidden rounded-[2rem] border border-pink-100 bg-white p-6 sm:p-8 lg:grid-cols-[0.8fr_1.2fr]"
      >
        <div className="relative">
          <div className="absolute -inset-3 rounded-[2rem] bg-oro-light/60 blur-xl" />
          <img
            src={IMAGES.gufetto}
            alt="Gufetto modellato a mano in pasta di zucchero, tra ghiande e foglie d'autunno"
            data-testid="gufetto-photo"
            className="relative aspect-[4/3] w-full rotate-1 rounded-3xl border-4 border-white object-cover object-[50%_40%] shadow-[0_18px_45px_rgba(45,36,36,0.14)]"
          />
        </div>
        <div>
          <Eyebrow>Da non perdere</Eyebrow>
          <h3 className="mt-2 font-display text-xl font-bold text-cacao sm:text-2xl">
            Il tutorial del Gufetto
          </h3>
          <p className="mt-2 text-sm text-cacao-muted leading-relaxed">
            Occhioni grandi e ali morbide: guarda come nasce il personaggio più
            saggio del bosco, passo dopo passo.
          </p>
          <div className="mt-5 overflow-hidden rounded-2xl border-4 border-white shadow-[0_14px_35px_rgba(45,36,36,0.12)]">
            <div className="relative aspect-video w-full">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${YOUTUBE.gufettoId}`}
                title="Video tutorial del Gufetto in pasta di zucchero — Le Allegre Pasticcione"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 h-full w-full"
                data-testid="gufetto-iframe"
              />
            </div>
          </div>
        </div>
      </div>
    </Reveal>

    <div className="mt-16">
      <Reveal>
        <Eyebrow>Archivio</Eyebrow>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-cacao sm:text-3xl">
          I biscotti e le decorazioni delle settimane precedenti
        </h2>
        <p className="mt-2 max-w-lg text-sm text-cacao-muted leading-relaxed">
          Tutti i personaggi già pubblicati: scegli il tuo preferito e segui la
          guida completa nell'Ebook dedicato.
        </p>
      </Reveal>
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {ARCHIVIO_BISCOTTI.map((b, i) => (
          <Reveal key={b.id} delay={i * 0.07}>
            <article
              data-testid={`archivio-card-${b.id}`}
              className="group flex h-full flex-col overflow-hidden rounded-3xl border border-pink-100 bg-white transition-all hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(45,36,36,0.12)]"
            >
              <div className="overflow-hidden">
                <img
                  src={IMAGES[b.image]}
                  alt={b.alt}
                  className={`aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105 ${b.pos || ""}`}
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-base font-bold text-cacao">{b.title}</h3>
                <p className="mt-1.5 flex-1 text-sm text-cacao-muted leading-relaxed">{b.text}</p>
                <a
                  data-testid={`archivio-cta-${b.id}`}
                  href={b.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/link mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-rosa-dark transition-colors hover:text-rosa"
                >
                  {b.cta}
                  <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover/link:translate-x-0.5" />
                </a>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>

    <Newsletter />
  </div>
);

export default Biscotti;
